import { PropsWithChildren, Suspense, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@givebutter/primitives/button';
import * as CampaignModal from 'components/campaign-modal';
import * as CreateCampaignDialog from 'components/create-campaign-dialog';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Import from 'components/imports/Import';
import { NucleoIcon, NucleoIconProps } from 'components/nucleo-icon';
import { IntercomProductTourTargets } from 'constants/intercom';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { twJoin } from 'tailwind-merge';

const QuickActionsMenu = () => {
    const { newCampaignCreationOverlay = false } = useFlags();
    const { accountId } = useCurrentAccount();
    const [isOpen, setIsOpen] = useState(false);

    return (
        <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenu.Trigger asChild>
                <Button
                    variant="outline"
                    color="neutral"
                    size="sm"
                    shape={'square'}
                    data-intercom-target={IntercomProductTourTargets.SidebarQuickActionsMenu}
                    aria-label="Quick actions"
                >
                    <NucleoIcon
                        icon="circlePlus"
                        className={twJoin('transform-gpu', isOpen && 'rotate-45')}
                    />
                </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
                align="start"
                sideOffset={4}
                onInteractOutside={() => setIsOpen(false)}
                className="max-w-72"
            >
                <DropdownMenu.Item asChild className="focus-visible:before:border-0">
                    <Link
                        to={`/accounts/${accountId}/contacts/new`}
                        className="block text-gray-800"
                    >
                        <QuickActionContainer>
                            <QuickActionIcon icon="userPlus" />
                            <QuickActionContentContainer>
                                <QuickActionTitle>Add a contact</QuickActionTitle>
                                <QuickActionDescription>
                                    Create a new contact
                                </QuickActionDescription>
                            </QuickActionContentContainer>
                        </QuickActionContainer>
                    </Link>
                </DropdownMenu.Item>

                <DropdownMenu.Item asChild className="focus-visible:before:border-0">
                    <Link
                        to={`/accounts/${accountId}/transactions/new`}
                        className="block text-gray-800"
                    >
                        <QuickActionContainer>
                            <QuickActionIcon icon="receipt" />
                            <QuickActionContentContainer>
                                <QuickActionTitle>New transaction</QuickActionTitle>
                                <QuickActionDescription>
                                    Manually add a new transaction
                                </QuickActionDescription>
                            </QuickActionContentContainer>
                        </QuickActionContainer>
                    </Link>
                </DropdownMenu.Item>

                {newCampaignCreationOverlay ? (
                    <CreateCampaignDialog.Root
                        triggerLocation={
                            CreateCampaignDialog.TriggerLocation.SidebarNavGlobalCreateNewItem
                        }
                    >
                        <CreateCampaignDialog.Trigger asChild>
                            <DropdownMenu.Item
                                className="focus-visible:before:border-0"
                                onSelect={e => e.preventDefault()}
                            >
                                <QuickActionContainer>
                                    <QuickActionIcon icon="bullhorn" />
                                    <QuickActionContentContainer>
                                        <QuickActionTitle>Create a campaign</QuickActionTitle>
                                        <QuickActionDescription>
                                            Start a new fundraising campaign
                                        </QuickActionDescription>
                                    </QuickActionContentContainer>
                                </QuickActionContainer>
                            </DropdownMenu.Item>
                        </CreateCampaignDialog.Trigger>
                    </CreateCampaignDialog.Root>
                ) : (
                    <CampaignModal.Root>
                        <CampaignModal.Trigger asChild>
                            <DropdownMenu.Item
                                className="focus-visible:before:border-0"
                                onSelect={e => e.preventDefault()}
                            >
                                <QuickActionContainer>
                                    <QuickActionIcon icon="bullhorn" />
                                    <QuickActionContentContainer>
                                        <QuickActionTitle>Create a campaign</QuickActionTitle>
                                        <QuickActionDescription>
                                            Start a new fundraising campaign
                                        </QuickActionDescription>
                                    </QuickActionContentContainer>
                                </QuickActionContainer>
                            </DropdownMenu.Item>
                        </CampaignModal.Trigger>
                    </CampaignModal.Root>
                )}

                <DropdownMenu.Sub>
                    <DropdownMenu.SubTrigger asChild>
                        <DropdownMenu.Item className="flex items-center">
                            <QuickActionContainer>
                                <QuickActionIcon icon="fileDownload" />
                                <QuickActionContentContainer>
                                    <QuickActionTitle>Import data</QuickActionTitle>
                                    <QuickActionDescription>
                                        Import from XLS or CSV files
                                    </QuickActionDescription>
                                </QuickActionContentContainer>
                            </QuickActionContainer>
                            <DropdownMenu.ItemArrow />
                        </DropdownMenu.Item>
                    </DropdownMenu.SubTrigger>
                    <DropdownMenu.Portal>
                        <DropdownMenu.SubContent>
                            <Suspense
                                fallback={<DropdownMenu.Item disabled>Contacts</DropdownMenu.Item>}
                            >
                                <Import.Root strategy="contacts">
                                    <Import.Trigger asChild={true}>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            Contacts
                                        </DropdownMenu.Item>
                                    </Import.Trigger>
                                    <Import.Portal>
                                        <Import.Space />
                                    </Import.Portal>
                                </Import.Root>
                            </Suspense>

                            <Suspense
                                fallback={
                                    <DropdownMenu.Item disabled>Transactions</DropdownMenu.Item>
                                }
                            >
                                <Import.Root strategy="transactions">
                                    <Import.Trigger asChild={true}>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            Transactions
                                        </DropdownMenu.Item>
                                    </Import.Trigger>
                                    <Import.Portal>
                                        <Import.Space />
                                    </Import.Portal>
                                </Import.Root>
                            </Suspense>

                            <Suspense
                                fallback={
                                    <DropdownMenu.Item disabled>Recurring plans</DropdownMenu.Item>
                                }
                            >
                                <Import.Root strategy="transaction_plans">
                                    <Import.Trigger asChild={true}>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            Recurring plans
                                        </DropdownMenu.Item>
                                    </Import.Trigger>
                                    <Import.Portal>
                                        <Import.Space />
                                    </Import.Portal>
                                </Import.Root>
                            </Suspense>
                        </DropdownMenu.SubContent>
                    </DropdownMenu.Portal>
                </DropdownMenu.Sub>
            </DropdownMenu.Content>
        </DropdownMenu.Root>
    );
};

const QuickActionContainer = ({ children }: PropsWithChildren) => {
    return <div className="flex gap-2">{children}</div>;
};

const QuickActionIcon = ({ icon }: { icon: NucleoIconProps['icon'] }) => {
    return (
        <div className="flex size-10 flex-shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-blue-500">
            <NucleoIcon icon={icon} />
        </div>
    );
};

const QuickActionContentContainer = ({ children }: PropsWithChildren) => {
    return <div className="flex flex-grow flex-col">{children}</div>;
};
const QuickActionTitle = ({ children }: PropsWithChildren) => {
    return <p className="text-sm font-medium text-gray-800">{children}</p>;
};

const QuickActionDescription = ({ children }: PropsWithChildren) => {
    return <p className="text-sm font-normal text-gray-800">{children}</p>;
};

export { QuickActionsMenu };
